import React, { FC, useCallback, useMemo } from 'react'
import s from './RestaurantReviews.module.sass'
import { Reviews } from '@entities/Restaurant/Reviews'
import { useAppSelector } from '@app/model/store'
import { useClientResponsive } from '@shared/lib/hooks/useClientResponsive'
import { Carousel } from '@shared/ui/Layout/Carousel'
import { SectionTitle } from '@shared/ui/Layout/SectionTitle'
import { useRouter } from 'next/router'
import { ErrorBoundary } from '@shared/lib/components/ErrorBoundary'
import { RestaurantReviewsSkeleton } from '@widgets/Restaurant/RestaurantReviews/index'
import { getIsActiveQA, TARGET_QA_KEY_URL } from '@shared/lib/helpers/autoTests/helpers'

type Props = {
  restaurant: {
    name: string
    id: number
  }
}

const RestaurantReviews: FC<Props> = ({ restaurant }) => {
  const stateReviews = useAppSelector((state) => state.entities.restaurant_reviews)
  const { user_reviews, other_reviews } = stateReviews.lists
  const { isMobile } = useClientResponsive()
  const router = useRouter()

  const mainList = isMobile ? [...user_reviews, ...other_reviews] : other_reviews

  const optionsSlider = {
    breakpoints: {
      '(min-width: 768px)': {
        disabled: true,
      },
    },
  }

  const getReviewsUrl = (thisQuery) => {
    const { city_slug, rest_slug } = thisQuery || {}

    if (!city_slug || !rest_slug || [city_slug, rest_slug].includes('undefined')) return null

    const isQA = getIsActiveQA()

    return !isQA
      ? `/${city_slug}/restaurant/${rest_slug}/reviews`
      : `/${city_slug}/restaurant/${rest_slug}/reviews?${TARGET_QA_KEY_URL}=true`
  }

  const getAnalyticData = useCallback(
    (type, index) => {
      if (type === 'user') return { section_index: index }
      if (type === 'main') return { section_index: (user_reviews.length || 0) + index }
    },
    [user_reviews.length, other_reviews.length],
  )

  if (!restaurant.id) return <RestaurantReviewsSkeleton />

  return (
    <ErrorBoundary>
      <div className={s.wrapper} id={'reviews'}>
        <Reviews restaurant={restaurant}>
          {!stateReviews.is_ready ? (
            <RestaurantReviewsSkeleton />
          ) : (
            <>
              <div className={s['wrapper-user-path']}>
                {!!user_reviews.length && !isMobile && (
                  <div>
                    <SectionTitle title={'Ваш отзыв'} />
                    {user_reviews.map((item, index) => (
                      <Reviews.Item
                        key={item.id}
                        data={item}
                        mode={isMobile ? 'card' : 'row'}
                        analytic={getAnalyticData('user', index)}
                      />
                    ))}
                  </div>
                )}

                <Reviews.Create mode={'block'} />
              </div>

              {!!mainList.length && (
                <div>
                  <SectionTitle
                    analytic={{ section_name: 'reviews' }}
                    title={isMobile ? 'Отзывы' : `Отзывы о ресторане ${restaurant.name}`}
                    link={getReviewsUrl(router.query) || undefined}
                  />

                  {!isMobile && <Reviews.Filters />}

                  <Carousel>
                    <Carousel.Slider className={s.list} optionsKeenSlider={optionsSlider}>
                      {mainList.map((item, index) => (
                        <Reviews.Item
                          key={item.id}
                          data={item}
                          mode={isMobile ? 'card' : 'row'}
                          analytic={getAnalyticData('main', index)}
                        />
                      ))}
                    </Carousel.Slider>
                  </Carousel>

                  {!isMobile && <Reviews.Pagination className={s.pagination} />}
                </div>
              )}
            </>
          )}
        </Reviews>
      </div>
    </ErrorBoundary>
  )
}

export default RestaurantReviews
